import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import { useTheme } from '@material-ui/core/styles';
import FilledInput from '@material-ui/core/FilledInput';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import ListItemText from '@material-ui/core/ListItemText';
import MuiSelect from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import PartCountLabel from '../PartCountLabel/PartCountLabel';
import { useInputStyles } from '../Input/styles';
import { useStyles, buildMenuProps } from './styles';
import useUpdateFilterValue from '../../../hooks/useUpdateFilterValue';
import { COLORS } from '../../../styles/constants';

const Select = ({
	minWidth,
	multiple,
	hiddenlabel,
	appliedValue,
	disabled,
	conditional,
	label,
	longLabel,
	values,
	cid,
	categoryId,
	handleSelectChange
}) => {
	const { t } = useTranslation();
	const classes = useStyles({ minWidth, multiple });
	const theme = useTheme();
	const inputClasses = useInputStyles();
	const labelRef = useRef();
	const [labelWidth, setLabelWidth] = useState(0);
	const { updateFilterValue } = useUpdateFilterValue();
	const appliedState = useSelector(state => state.filters.appliedState);
	const countOfPartsForFilterOptions = useSelector(state => state.filters.countOfPartsForFilterOptions || {});
	useEffect(() => {
		if (labelRef.current) setLabelWidth(labelRef.current.clientWidth);
	}, []);

	const handleChange = event => {
		if (cid) {
			updateFilterValue(cid, categoryId, event.target.value);
		} else {
			handleSelectChange(event.target.value);
		}
	};

	return (
		<FormControl variant="filled" margin="dense" className={classes.formControl}>
			{hiddenlabel || (
				<InputLabel ref={labelRef} id={label + '_select_input_label'} htmlFor={label + '-select-input'}>
					<Grid item>
						{longLabel && (
							<Typography display="inline" variant="body1">
								{parse(longLabel) + '\u00a0\u00a0'}
							</Typography>
						)}

						<Typography display="inline" variant="body1">
							{parse(label)}
						</Typography>
					</Grid>
				</InputLabel>
			)}

			<MuiSelect
				labelId={label + '_select_input_label'}
				id={label + '_select'}
				disabled={disabled}
				value={appliedValue}
				onChange={handleChange}
				onClose={() => {
					setTimeout(() => {
						document.activeElement.blur();
					}, 0);
				}}
				input={
					<FilledInput
						labelWidth={labelWidth}
						style={{
							border: appliedState[cid]?.used ? `2px solid ${COLORS.PRIMARY}` : 'none'
						}}
						id={label + '-select-input'}
						classes={inputClasses}
					/>
				}
				multiple={multiple}
				renderValue={multiple && (selected => selected.join(', '))}
				MenuProps={buildMenuProps(theme, minWidth)}>
				{values.map((value, index) => {
					const menuItem =
						value.hasOwnProperty('label') && value.hasOwnProperty('value')
							? value
							: conditional === true
							? { label: value.label, value: value.label }
							: { label: value, value: value };

					const partCountExist = countOfPartsForFilterOptions[cid] && countOfPartsForFilterOptions[cid][menuItem.label];
					const partCount = partCountExist ? countOfPartsForFilterOptions[cid][menuItem.label].count : 0;
					const isSelected = multiple ? appliedValue.indexOf(menuItem.label) !== -1 : appliedValue === menuItem.label;
					const disableByPartcount = partCountExist && partCount === 0;
					if (!disableByPartcount || isSelected) {
						return (
							<MenuItem
								key={menuItem.value + index + '-select-item'}
								value={menuItem.value}
								className={classes.menuItem}>
								{multiple && <Checkbox color="primary" checked={appliedValue.indexOf(menuItem.value) > -1} />}
								<ListItemText
									primary={
										<Typography variant="body2">
											{i18next.exists(`filterValue.${menuItem.label}`)
												? t(`filterValue.${menuItem.label}`)
												: parse(menuItem.label)}
											{partCountExist && <PartCountLabel count={partCount} />}
										</Typography>
									}
									className={classes.listItemText}
								/>
							</MenuItem>
						);
					} else {
						return null;
					}
				})}
			</MuiSelect>
		</FormControl>
	);
};

export default Select;

Select.propTypes = {
	minWidth: PropTypes.number,
	multiple: PropTypes.bool,
	hiddenlabel: PropTypes.bool,
	appliedValue: PropTypes.arrayOf(PropTypes.string).isRequired,
	disabled: PropTypes.bool.isRequired,
	conditional: PropTypes.bool,
	label: PropTypes.string.isRequired,
	values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool])).isRequired
};
